import { Update, defModule } from '@st/redux'
import { Effect, pipe } from 'effect'
import { match, P } from '@st/pattern'
import { SupportChat, type SupportChatLauncherConfig } from '~/effects'
import { observeLocation } from '~/features/module-runtime'
import { routes, type AppRoute } from '~/features/routes'

type SupportChatState =
  | { status: 'waitingForLocation' }
  | { status: 'configured'; config: SupportChatLauncherConfig }

type SupportChatMessage = {
  type: 'configurationChanged'
  config: SupportChatLauncherConfig
}

const observeSupportChatConfiguration = observeLocation(
  ({ pathname }): SupportChatMessage => ({
    type: 'configurationChanged',
    config: supportChatConfigForLocation(pathname, routes.match(pathname) ?? null)
  })
)

export const supportChatModule = defModule<{
  state: SupportChatState
  message: SupportChatMessage
}>({
  init: () => ({ status: 'waitingForLocation' }),
  tasks: () => [observeSupportChatConfiguration()],
  update: pipe(updateSupportChat, Update.ignoreWhen(configurationUnchanged))
})

function updateSupportChat(_state: SupportChatState, message: SupportChatMessage) {
  return [
    { status: 'configured' as const, config: message.config },
    configureSupportChat(message.config)
  ] as const
}

function configurationUnchanged({
  state,
  message
}: {
  state: SupportChatState
  message: SupportChatMessage
}) {
  return state.status == 'configured' && sameSupportChatConfig(state.config, message.config)
}

const DEFAULT_LAUNCHER_CONFIG = {
  visible: true,
  bottom: 20,
  right: 20
} satisfies SupportChatLauncherConfig

const PAGE_LAUNCHER_CONFIGS = [
  { path: '/preview-organizer', bottom: 64 },
  { path: '/settings/client-organizer', bottom: 64 },
  { path: '/import', bottom: 64 },
  { path: '/binders/', bottom: 64 }
] as const

function supportChatConfigForLocation(
  pathname: string,
  route: AppRoute | null
): SupportChatLauncherConfig {
  const pageConfig = PAGE_LAUNCHER_CONFIGS.find((config) => pathname.includes(config.path))

  return {
    visible: routeAllowsSupportChat(route),
    bottom: pageConfig?.bottom ?? DEFAULT_LAUNCHER_CONFIG.bottom,
    right: DEFAULT_LAUNCHER_CONFIG.right
  }
}

function routeAllowsSupportChat(route: AppRoute | null) {
  return match(route)
    .with(
      {
        name: P.union('folder_list_send_client_organizers', 'folder_list_organizer_reminders')
      },
      () => false
    )
    .otherwise(() => true)
}

function sameSupportChatConfig(left: SupportChatLauncherConfig, right: SupportChatLauncherConfig) {
  return left.visible == right.visible && left.bottom == right.bottom && left.right == right.right
}

function configureSupportChat(config: SupportChatLauncherConfig) {
  return Effect.gen(function* () {
    const supportChat = yield* SupportChat
    yield* supportChat.configureLauncher(config)
    return null
  })
}
